<template>
    <div class="h-full flex flex-col space-y-4">
        <div class="flex items-center justify-between space-x-2">
            <div class="flex items-center space-x-3">
                <UButton to="/broadcasts" color="white" icon="i-ph-arrow-left" size="sm" />
                <h1 class="text-2xl font-semibold text-gray-700">Suppressions</h1>
            </div>
            <UButton @click="overlays.add = true" color="black" icon="i-ph-plus-bold">
                Add Suppression
            </UButton>
        </div>

        <div class="flex items-center space-x-2">
            <USelectMenu
                v-model="channelFilter"
                :options="channelOptions"
                option-attribute="label"
                value-attribute="value"
                size="sm"
                color="white"
                class="w-40"
            />
            <UInput
                v-model="search"
                placeholder="Search contact…"
                icon="i-ph-magnifying-glass"
                size="sm"
                class="w-72"
                @update:model-value="onSearch"
            />
        </div>

        <div class="flex-1 bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
            <UTable
                :rows="rows"
                :columns="columns"
                :loading="loading"
                :loading-state="{ icon: 'i-ph-circle-notch-bold', label: '' }"
                :empty-state="{ icon: 'i-ph-prohibit-duotone', label: 'No suppressions.' }"
                :ui="{ thead: 'sticky top-0 bg-white z-10 shadow-sm', td: { padding: 'py-3' } }"
                class="h-full"
            >
                <template #channel-data="{ row }">
                    <UBadge :color="channelColor(row.channel)" size="sm" variant="subtle">{{ row.channel }}</UBadge>
                </template>
                <template #reason-data="{ row }">
                    <UBadge v-if="row.reason" :color="reasonColor(row.reason)" size="sm" variant="subtle">{{ row.reason }}</UBadge>
                    <span v-else class="text-gray-400 text-sm">—</span>
                </template>
                <template #source-data="{ row }">
                    <span class="text-sm text-gray-500">{{ row.source ?? '—' }}</span>
                </template>
                <template #created_at-data="{ row }">
                    <span class="text-sm text-gray-500">{{ formatDate(row.created_at) }}</span>
                </template>
                <template #actions-data="{ row }">
                    <UButton
                        color="red"
                        variant="ghost"
                        icon="i-ph-trash-fill"
                        size="xs"
                        :loading="deletingId === row.id"
                        @click="deleteRow(row)"
                    />
                </template>
            </UTable>
        </div>

        <TablePagination :pagination="pagination" class="shrink-0" />

        <USlideover v-model="overlays.add" side="right">
            <div class="flex flex-col h-full p-6 space-y-6">
                <div class="flex items-center justify-between">
                    <h2 class="text-lg font-semibold text-gray-700">Add Suppression</h2>
                    <UButton color="gray" variant="ghost" icon="i-ph-x" @click="overlays.add = false" />
                </div>
                <UFormGroup label="Channel" required>
                    <USelectMenu
                        v-model="form.channel"
                        :options="channelOptions.slice(1)"
                        option-attribute="label"
                        value-attribute="value"
                        placeholder="Select channel"
                    />
                </UFormGroup>
                <UFormGroup label="Contact" required>
                    <UInput v-model="form.contact" placeholder="Email, phone number, or push token" />
                </UFormGroup>
                <div class="flex justify-end space-x-2 pt-2">
                    <UButton color="white" @click="overlays.add = false">Cancel</UButton>
                    <UButton
                        color="black"
                        :loading="saving"
                        :disabled="!form.channel || !form.contact.trim()"
                        @click="addSuppression"
                    >
                        Add
                    </UButton>
                </div>
            </div>
        </USlideover>
    </div>
</template>

<script setup lang="ts">
import { UserRole } from '~/types/user'
import { normalizeContact } from '~/utils/broadcast-cost'
import type { BroadcastChannel } from '~/types/broadcast-message'

definePageMeta({ layout: 'default', middleware: 'auth', roles: [UserRole.admin] })
useHead({ title: 'Suppressions' })

const client = useSupabaseClient()
const pagination = usePagination()
const toast = useToast()

const loading = ref(false)
const saving = ref(false)
const deletingId = ref<string | null>(null)
const search = ref('')
const channelFilter = ref<string>('all')
let searchTimer: ReturnType<typeof setTimeout> | null = null

type Suppression = {
    id: string
    channel: BroadcastChannel
    contact: string
    customer_id: string | null
    reason: string | null
    source: string | null
    created_at: string
}

const rows = ref<Suppression[]>([])

const overlays = reactive({ add: false })
const form = reactive<{ channel: BroadcastChannel | ''; contact: string }>({
    channel: '',
    contact: '',
})

const channelOptions = [
    { label: 'All Channels', value: 'all' },
    { label: 'Email', value: 'email' },
    { label: 'SMS', value: 'sms' },
    { label: 'WhatsApp', value: 'whatsapp' },
    { label: 'Push', value: 'push' },
]

const columns = [
    { key: 'channel', label: 'Channel' },
    { key: 'contact', label: 'Contact', class: 'w-full' },
    { key: 'reason', label: 'Reason' },
    { key: 'source', label: 'Source' },
    { key: 'created_at', label: 'Added' },
    { key: 'actions', class: 'w-px' },
]

onMounted(() => fetchData())
watch(() => pagination.from, () => fetchData())
watch(channelFilter, () => {
    pagination.page = 0
    fetchData()
})

function onSearch() {
    if (searchTimer) clearTimeout(searchTimer)
    searchTimer = setTimeout(() => {
        pagination.page = 0
        fetchData()
    }, 300)
}

async function fetchData() {
    loading.value = true
    rows.value = []
    pagination.setTotal(undefined)

    let query = client
        .from('channel_suppressions')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(pagination.from, pagination.to)

    if (channelFilter.value !== 'all') {
        query = query.eq('channel', channelFilter.value)
    }
    if (search.value.trim()) {
        query = query.ilike('contact', `%${search.value.trim()}%`)
    }

    const { count, data } = await query.returns<Suppression[]>()

    if (count !== null && data) {
        rows.value = data
        pagination.setTotal(count)
    }

    loading.value = false
}

async function addSuppression() {
    if (!form.channel || !form.contact.trim()) return
    saving.value = true

    const contact = normalizeContact(form.channel as BroadcastChannel, form.contact.trim())

    const { error } = await client.from('channel_suppressions').insert({
        channel: form.channel,
        contact,
        reason: 'manual',
        source: 'admin',
    } as never)

    saving.value = false

    if (error) {
        toast.add({ title: 'Failed to add suppression', description: error.message, color: 'red' })
        return
    }

    overlays.add = false
    form.channel = ''
    form.contact = ''
    fetchData()
}

async function deleteRow(row: Suppression) {
    deletingId.value = row.id

    const { error } = await client.from('channel_suppressions').delete().eq('id', row.id)

    deletingId.value = null

    if (error) {
        toast.add({ title: 'Failed to delete suppression', description: error.message, color: 'red' })
        return
    }

    fetchData()
}

function channelColor(channel: string) {
    return { email: 'blue', sms: 'green', whatsapp: 'emerald', push: 'purple' }[channel] ?? 'gray'
}

function reasonColor(reason: string) {
    return {
        user_optout: 'orange',
        twilio_stop: 'amber',
        bounce: 'red',
        complaint: 'red',
        manual: 'gray',
    }[reason] ?? 'gray'
}

function formatDate(date: string): string {
    return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}
</script>
